import React from "react";
import { useNavigate } from "react-router-dom";
import { PATH_CART, PATH_HOME } from "@routes";
import styled from "styled-components";
import { breakpoint, theme } from "@utilities";

interface RedirectToCartProps {
    open: boolean;
    onClose: () => void;
    /**
     * Where "Go to cart" leads. A guest's cart lives on a session, so the caller
     * passes the session-qualified URL; signed-in visitors get the plain path.
     */
    cartUrl?: string;
}

/**
 * Shown the moment something is added to the cart, offering the only two things
 * anyone wants next.
 *
 * Raised where the add happened rather than after a redirect: being asked "view
 * your cart?" on a page you did not choose to be on answers the question for you.
 *
 * Deliberately built to the same shape as the sign-up modals — same dialog width,
 * logo, headline scale and full-width stacked buttons — so the two interruptions
 * a visitor can meet on a product page look like one product rather than two.
 */
export const RedirectToCart: React.FC<RedirectToCartProps> = ({ open, onClose, cartUrl }) => {
    const navigate = useNavigate();

    const handleGoToCart = () => {
        onClose();
        navigate(cartUrl || PATH_CART);
    };
    const handleContinueShopping = () => {
        onClose();
        navigate(PATH_HOME);
    };

    return open ? (
        <>
            <StyledOverlay onClick={onClose} />
            <StyledDialog onClick={(e) => e.stopPropagation()}>
                <BrandLogo src="/images/logos/Primary_logo_color.svg" alt="" />
                <StyledTitle>Added to your cart</StyledTitle>
                <StyledQuestionText>What would you like to do next?</StyledQuestionText>

                <PrimaryButton type="button" onClick={handleGoToCart}>
                    Go to cart
                </PrimaryButton>
                <SecondaryButton type="button" onClick={handleContinueShopping}>
                    Continue shopping
                </SecondaryButton>
            </StyledDialog>
        </>
    ) : null;
};

const StyledOverlay = styled.div`
    position: fixed;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.45);
    /* Above the availability panel's own action bar (100) and the header (40),
       both of which would otherwise sit on top of this. */
    z-index: 1200;
`;

const StyledDialog = styled.div`
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1201;
    width: 420px;
    max-width: calc(100vw - 32px);
    box-sizing: border-box;
    padding: 28px 24px 24px;
    border-radius: ${theme.borderRadius.large};
    background: ${theme.colors.white};
    display: flex;
    flex-direction: column;
    align-items: center;

    ${breakpoint.breakOnlyMobile`
        padding: 24px 16px 20px;
    `}
`;

const BrandLogo = styled.img`
    width: 176px;
    height: auto;
    margin-bottom: 8px;
`;

const StyledTitle = styled.p`
    margin: 0 0 6px;
    font-size: 24px;
    font-weight: 700;
    text-align: center;
    color: ${theme.colors.black};
`;

const StyledQuestionText = styled.p`
    margin: 0 0 18px;
    font-size: 14px;
    text-align: center;
    color: ${theme.colors.gray_1};
`;

const dialogButton = `
    width: 100%;
    height: 48px;
    border-radius: 4px;
    font-size: 17px;
    font-weight: 700;
    cursor: pointer;
`;

const PrimaryButton = styled.button`
    ${dialogButton}
    border: none;
    background: ${theme.colors.main};
    color: ${theme.colors.button_text};

    &:hover {
        opacity: 0.9;
    }
`;

const SecondaryButton = styled.button`
    ${dialogButton}
    margin-top: 10px;
    border: 1px solid ${theme.colors.gray_5};
    background: ${theme.colors.white};
    color: ${theme.colors.black};

    &:hover {
        background: ${theme.colors.gray_6};
    }
`;
